import { StyleSheet } from 'react-native';
import { spacing, ticketColors, ticketFonts, typography } from '../theme/public-api';

export const SURFACE_CARD_RADIUS_FILTER = 14;
export const SURFACE_CARD_RADIUS_CONTENT = 14;
export const SURFACE_CARD_RADIUS_CONTROL = 10;

export const surfaceCardStyles = StyleSheet.create({
  denseListWrap: {
    flexDirection: 'row',
    borderRadius: SURFACE_CARD_RADIUS_CONTENT,
    overflow: 'hidden',
    marginBottom: spacing.sm,
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    alignSelf: 'stretch',
  },
  denseListAccentNeutral: {
    width: 3,
    backgroundColor: ticketColors.rule,
  },
  denseListAccentAmber: {
    width: 3,
    backgroundColor: ticketColors.gold,
    opacity: 0.55,
  },
  denseListBody: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
    gap: spacing.sm,
  },
  heroCard: {
    backgroundColor: ticketColors.paper,
    borderRadius: SURFACE_CARD_RADIUS_CONTENT,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    padding: spacing.sm + 2,
    gap: spacing.xs,
    alignSelf: 'stretch',
  },
  kpiShell: {
    alignSelf: 'stretch',
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
    borderRadius: SURFACE_CARD_RADIUS_CONTENT,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    gap: spacing.sm,
  },
  kpiHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  kpiHeaderTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: 11,
    fontWeight: '800',
    color: ticketColors.ink3,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
  },
  kpiHeaderHint: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
    fontSize: 12,
    flexShrink: 0,
  },
  kpiSubsection: {
    marginTop: spacing.xs,
    paddingTop: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    gap: spacing.sm,
    alignSelf: 'stretch',
  },
  kpiGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  kpiCell: {
    flexGrow: 1,
    minWidth: '46%',
    borderRadius: SURFACE_CARD_RADIUS_CONTROL,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    gap: 4,
  },
  kpiCellWide: {
    flex: 1,
    minWidth: '30%',
  },
  kpiCellWrap: {
    minWidth: '100%',
  },
  kpiCellLabel: {
    fontSize: 10,
    fontWeight: '800',
    color: ticketColors.ink3,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
  },
  kpiCellValue: {
    ...typography.titleLg,
    color: ticketColors.ink,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
  },
  kpiCellValueWrap: {
    fontSize: 16,
    lineHeight: 22,
    flexShrink: 1,
  },
  embeddedMetricShell: {
    paddingTop: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    gap: spacing.sm,
    alignSelf: 'stretch',
  },
  embeddedMetricHeader: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: spacing.xs,
  },
  embeddedMetricTitle: {
    fontSize: 11,
    fontWeight: '800',
    color: ticketColors.ink3,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
  },
  embeddedMetricHint: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
    fontSize: 12,
    flexShrink: 1,
  },
  embeddedMetricRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.lg,
    alignItems: 'flex-start',
  },
  embeddedMetricFooter: {
    gap: spacing.xs,
  },
  embeddedMetric: {
    flex: 1,
    minWidth: 120,
    gap: 4,
  },
  embeddedMetricWarn: {
    paddingLeft: spacing.sm,
    borderLeftWidth: 3,
    borderLeftColor: ticketColors.gold,
  },
  embeddedMetricLabel: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
    fontSize: 12,
  },
  embeddedMetricValue: {
    ...typography.titleLg,
    color: ticketColors.ink,
    fontWeight: '800',
    fontVariant: ['tabular-nums'],
  },
  embeddedMetricValueWarn: {
    color: ticketColors.gold,
  },
  embeddedMetricValueAccent: {
    color: ticketColors.gold,
  },
  embeddedMetricValueCompact: {
    fontSize: 14,
    lineHeight: 18,
    fontWeight: '700',
  },
  /** Long money strings: allow the value to shrink inside its column. */
  embeddedMetricValueFit: {
    width: '100%',
    minWidth: 0,
    flexShrink: 1,
  },
  embeddedMetricTrack: {
    marginTop: spacing.xs,
    height: 3,
    borderRadius: 999,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
    alignSelf: 'stretch',
  },
  embeddedMetricFill: {
    height: '100%',
    borderRadius: 999,
    backgroundColor: ticketColors.gold,
  },
  embeddedMetricFillLimit: {
    backgroundColor: ticketColors.gold,
  },
  embeddedMetricFillOver: {
    backgroundColor: ticketColors.gold,
  },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    borderRadius: 999,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderWidth: 1,
  },
  statusPillCompact: {
    paddingVertical: 3,
  },
  statusPillSuccess: {
    borderColor: 'rgba(74, 222, 128, 0.42)',
    backgroundColor: 'rgba(74, 222, 128, 0.09)',
  },
  statusPillWarning: {
    borderColor: 'rgba(250, 204, 21, 0.45)',
    backgroundColor: 'rgba(250, 204, 21, 0.1)',
  },
  statusPillDanger: {
    borderColor: 'rgba(248, 113, 113, 0.45)',
    backgroundColor: 'rgba(248, 113, 113, 0.1)',
  },
  statusPillNeutral: {
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  statusPillService: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
  },
  statusPillText: {
    fontSize: 12,
    fontWeight: '700',
    flexShrink: 1,
  },
  statusPillTextSuccess: {
    color: ticketColors.ok,
  },
  statusPillTextWarning: {
    color: ticketColors.gold,
  },
  statusPillTextDanger: {
    color: ticketColors.bad,
  },
  statusPillTextNeutral: {
    color: ticketColors.ink2,
  },
  statusPillTextService: {
    color: ticketColors.goldInk,
  },
  denseListTitle: {
    color: ticketColors.ink,
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 19,
  },
  denseListMeta: {
    marginTop: 2,
    color: ticketColors.ink3,
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 0.2,
  },
  blockHeading: {
    marginTop: spacing.lg,
    marginBottom: spacing.xs,
  },
  blockHeadingLabel: {
    fontSize: 11,
    fontWeight: '700' as const,
    color: ticketColors.ink3,
    letterSpacing: 0.5,
    textTransform: 'uppercase' as const,
  },
  blockHeadingDescription: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 19,
    marginTop: 4,
  },
  denseBlockTitle: {
    ...typography.titleLg,
    fontSize: 16,
    color: ticketColors.ink,
    fontWeight: '600' as const,
  },
  surfaceCaption: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: ticketColors.paper,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    minWidth: 0,
    alignSelf: 'flex-start' as const,
  },
  secondaryButtonText: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '600',
    flexShrink: 1,
  },
  productGridCard: {
    backgroundColor: ticketColors.paper2,
    borderRadius: SURFACE_CARD_RADIUS_CONTENT,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    flex: 1,
    minHeight: 120,
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
    gap: spacing.xs,
  },
  productGridCardCompact: {
    minHeight: 96,
    padding: spacing.sm,
    marginBottom: spacing.xs,
  },
  productAddCue: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  productAddCueCompact: {
    width: 28,
    height: 28,
  },
  productGridName: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 15,
    lineHeight: 20,
  },
  productGridNameCompact: {
    fontSize: 14,
    lineHeight: 17,
  },
  productGridSku: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '500',
    fontSize: 12,
    letterSpacing: 0.2,
  },
  productGridSkuCompact: {
    fontSize: 11,
    lineHeight: 13,
  },
  productGridPrice: {
    ...typography.titleLg,
    color: ticketColors.ink,
    textAlign: 'right',
    flexShrink: 0,
    marginLeft: spacing.xs,
    fontWeight: '800',
    fontSize: 19,
  },
  productGridPriceCompact: {
    fontSize: 18,
    lineHeight: 22,
  },
});

export function statusPillToneStyle(tone: StatusPillTone) {
  switch (tone) {
    case 'success':
      return { pill: surfaceCardStyles.statusPillSuccess, text: surfaceCardStyles.statusPillTextSuccess };
    case 'warning':
      return { pill: surfaceCardStyles.statusPillWarning, text: surfaceCardStyles.statusPillTextWarning };
    case 'danger':
      return { pill: surfaceCardStyles.statusPillDanger, text: surfaceCardStyles.statusPillTextDanger };
    case 'service':
      return { pill: surfaceCardStyles.statusPillService, text: surfaceCardStyles.statusPillTextService };
    case 'neutral':
    default:
      return { pill: surfaceCardStyles.statusPillNeutral, text: surfaceCardStyles.statusPillTextNeutral };
  }
}

export type StatusPillTone = 'success' | 'warning' | 'danger' | 'neutral' | 'service';
